import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toggleMobileMenu, toggleSidebar } from '../../store/slices/layoutSlice';
import { setKeyword } from '../../store/slices/searchSlice';
import { RootState } from '../../store';
import styles from './Header.module.css';
import logo from '../../assets/images/logo.svg';
import logoShort from '../../assets/images/logo_short.png';

const Header: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // 获取当前路径
  const [searchValue, setSearchValue] = useState('');
  const { mobileMenuOpen } = useSelector((state: RootState) => state.layout);
  const [isMobile, setIsMobile] = useState(false);
  const [hasSetHome, setHasIsSetHome] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // 添加toast状态
  const [showToast, setShowToast] = useState(false);
  const [showSetHomeButton, setShowSetHomeButton] = useState(false);

  useEffect(() => {
    // 检查URL参数isgogolily是否为true
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const isGogolily = urlParams.get('isgogolily') === 'true';
      const isMobile = window.innerWidth <= 576;
      const packageName = window.BsSDK && window.BsSDK.SDKMgr && window.BsSDK.SDKMgr.getPackageName && window.BsSDK.SDKMgr.getPackageName();
      setShowSetHomeButton((packageName === 'com.broadsolutionsgroup.amazinglitedrama' || isGogolily) && isMobile);
    }
  }, []);

  // 检测是否为移动设备
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 576);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // 检查链接是否处于活动状态
  const isActive = (path: string): boolean => {
    if (path === '/' && location.pathname === '/') {
      return true;
    }
    if (path !== '/' && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 无论搜索框是否为空，都导航到搜索页面
    // 如果有搜索关键词，则设置到store中
    if (searchValue.trim()) {
      dispatch(setKeyword(searchValue.trim()));
      navigate(`/search?keyword=${encodeURIComponent(searchValue.trim())}`);
    } else {
      dispatch(setKeyword(''));
      navigate('/search');
    }
  };

  // 设置为主页
  const handleSetHomePage = () => {
    // 确保只在客户端执行
    if (typeof window === 'undefined') {return;}

    if (isHomePage() || hasSetHome) {
      setToastMessage('Homepage already set to Game.');
      setShowToast(true);
      return;
    }
    
    // 将当前页面设置为主页
    const homepage = window.location.origin + window.location.pathname + '?isgogolily=true';
    if (window.BsSDK && window.BsSDK.SDKMgr && typeof window.BsSDK.SDKMgr.setHome === 'function') {
      window.BsSDK.SDKMgr.setHome(homepage);
    }
    localStorage.setItem('homepage', homepage);
    // 显示确认消息
    setToastMessage('Homepage successfully set to Game.');
    setShowToast(true);
    setHasIsSetHome(true);
  };

  // 检查当前页面是否为主页
  const isHomePage = () => {
    // 只在客户端执行
    if (typeof window === 'undefined') {return false;}
    
    const path = window.location.hostname + window.location.pathname;
    return window.BsSDK && window.BsSDK.SDKMgr && window.BsSDK.SDKMgr.getHome && window.BsSDK.SDKMgr.getHome().indexOf(path) > -1;
  };

  // 处理Toast显示
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000); // 3秒后自动隐藏

      return () => clearTimeout(timer);
    }
  }, [showToast]);

  return (
    <header className={styles.header}>
      <div className={`${styles.container} ${showSetHomeButton ? styles.containerWithSetHome : ''}`}>
        <div className={styles.logoSection}>
          <button 
            className={styles.menuToggle} 
            onClick={(e) => {
              e.stopPropagation(); // 阻止事件冒泡
              console.log('Menu button clicked');
              dispatch(toggleSidebar());
              if (mobileMenuOpen) {
                dispatch(toggleMobileMenu());
              }
            }}
            aria-label="Toggle sidebar"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          
          {/* 在非移动端显示logo */}
          {!isMobile && (
            <a href="/" className={styles.logo}>
              <img src={logo} alt="PopupJoyLand" className={styles.logoImage} />
            </a>
          )}
        </div>

        {/* 移动端居中显示logo */}
        {isMobile && (
          <div className={styles.centerLogo}>
            <a href="/" className={styles.logo}>
              <img src={showSetHomeButton ? logoShort : logo} alt="PopupJoyLand" className={styles.logoImage} />
            </a>
          </div>
        )}

        {showSetHomeButton && (
          <div className={styles.menuItems}>
            <button 
              className={`${styles.setHomePageButton} ${isHomePage() || hasSetHome ? styles.selected : ''}`} 
              onClick={handleSetHomePage}
            >
              Set As Home
            </button>
            <a className={styles.menuItemLink} href="https://joyloopgame.com/anims/?isgogolily=true">Game</a>
          </div>
        )}

        {/* 在非移动端显示搜索框 */}
        {!isMobile && (
          <form className={styles.searchForm} onSubmit={handleSearchSubmit}>
            <input
              type="text"
              placeholder="Search for dramas, videos..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className={styles.searchInput}
            />
            <button type="submit" className={styles.searchButton} aria-label="Search">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 14L11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </form>
        )}

        {/* 在非移动端显示导航菜单 */}
        {!isMobile && (
          <div className={styles.navSection}>
            <nav className={`${styles.nav} ${mobileMenuOpen ? styles.navOpen : ''}`}>
              <ul className={styles.navList}>
                <li><a href="/" className={`${styles.navLink} ${isActive('/') ? styles.active : ''}`}>Home</a></li>
                <li><a href="/search?category=New Release" className={`${styles.navLink} ${location.pathname === '/search' && location.search.includes('category=New%20Release') ? styles.active : ''}`}>New Releases</a></li>
                <li><a href="/search?category=Trending Now" className={`${styles.navLink} ${location.pathname === '/search' && location.search.includes('category=Trending%20Now') ? styles.active : ''}`}>Trending</a></li>
              </ul>
            </nav>
            
            <button 
              className={styles.mobileMenuToggle}
              onClick={() => dispatch(toggleMobileMenu())}
              aria-label="Toggle mobile menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        )}
      </div>
      {/* Toast 提示 */}
      <div className={`${styles.toast} ${showToast ? styles.show : ''}`}>
        {toastMessage}
      </div>
    </header>
  );
};

export default Header; 